import { useEffect, useState } from "react";
import {
  ApiRequestOptions,
  sendAuthenticatedRequest,
} from "../common/functions";
import CustomerDetailsForm from "../common/components/CustomerDetailsForm";
import SignupStepIndicator from "../common/components/SignupStepIndicator";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import {
  CustomerFieldsConfig,
  CustomerProfile,
  FALLBACK_CUSTOMER_FIELDS,
} from "../common/models/customerFields";
import { useMiscStore, useUser } from "../common/stateDefs";

/**
 * The details step of signing up. Which fields it asks for comes from Internal >
 * Tools > Customer Data Configuration, so this screen and the account page under
 * My information always ask for the same things.
 */
export default function CompanyDetailsForm() {
  const setCompanyDetailsRequired = useMiscStore(
    (state) => state.setCompanyDetailsRequired,
  );
  const user = useUser((state) => state.user);
  const setUser = useUser((state) => state.setUser);
  const [config, setConfig] = useState<CustomerFieldsConfig | null>(null);
  // The request needs a token: asked for before the sign-in has been restored it
  // comes back 403, and this screen would then quietly ask for the old fields.
  const { authUser, authIsLoading } = useFirebaseAuthentication();

  useEffect(() => {
    if (authIsLoading || !authUser) return;
    let cancelled = false;
    sendAuthenticatedRequest(
      `${process.env.REACT_APP_USER_API_BASE_URL}/customer-fields`,
      { method: "GET" },
    )
      .then((resp) => (resp.ok ? resp.json() : null))
      .then((data) => {
        if (!cancelled) setConfig(data?.config || FALLBACK_CUSTOMER_FIELDS);
      })
      .catch(() => {
        // A new customer should not be stuck on a blank form because one
        // configuration call failed; ask for what we always asked for.
        if (!cancelled) setConfig(FALLBACK_CUSTOMER_FIELDS);
      });
    return () => {
      cancelled = true;
    };
  }, [authIsLoading, authUser]);

  return (
    <div className="mx-auto md:w-[30em] px-4 py-10">
      <SignupStepIndicator currentStep={2} />
      {!config ? (
        <p className="text-sm text-gray-600">Loading…</p>
      ) : (
        <CustomerDetailsForm
          config={config}
          addressLookup
          submitLabel="Submit"
          fieldsHeading={
            <p className="mb-2">Please provide your company details</p>
          }
          lookupHeading={
            <>
              <p className="mb-4">Welcome to Kono!</p>
              <p className="mb-4">
                Please enter your company's address. We use this to determine
                the best shipping options for you.
              </p>
            </>
          }
          onSkip={async () => {
            try {
              await sendAuthenticatedRequest(
                `${process.env.REACT_APP_USER_API_BASE_URL}/company/skip`,
                { method: "POST", jsonBody: {} },
              );
            } catch {
              // Still let them in; a refresh may ask again if the write failed.
            }
            if (user) {
              setUser({ ...user, companyOnboardingSkipped: true });
            }
            setCompanyDetailsRequired(false);
          }}
          skipLabel="Skip for now — go to the platform"
          onSubmit={async (profile: CustomerProfile) => {
            const requestOptions: ApiRequestOptions = {
              method: "POST",
              jsonBody: { profile },
            };
            const resp = await sendAuthenticatedRequest(
              `${process.env.REACT_APP_USER_API_BASE_URL}/company`,
              requestOptions,
            );
            if (!resp.ok) {
              const data = await resp.json().catch(() => ({}));
              throw new Error(
                data.error || "Something went wrong submitting your details.",
              );
            }
            // The user document is what the parent PortalContainer watches, so
            // it moves on by itself once the write lands.
            setCompanyDetailsRequired(false);
            return true;
          }}
        />
      )}
    </div>
  );
}
